import type { ProjectSearchResponse } from "@racler/protocol";
import { splitHighlight, splitPath, type SearchItem } from "../lib/projectSearch.ts";

/** Results grouped by file: file name + Project-relative path, then matching lines with the match highlighted. */
export function ProjectSearchResults({
  response,
  items,
  selected,
  onOpen,
}: {
  response: ProjectSearchResponse;
  items: readonly SearchItem[];
  selected: number;
  onOpen: (item: SearchItem) => void;
}) {
  const indexOf = new Map(items.map((it, i) => [it.key, i] as const));
  return (
    <>
      {response.files.map((f) => {
        const { name, dir } = splitPath(f.path);
        const fileItem: SearchItem = { kind: "file", key: `f:${f.path}`, path: f.path };
        const fileIdx = indexOf.get(fileItem.key) ?? -1;
        return (
          <div key={f.path} className="project-search-file">
            <div
              role={fileIdx >= 0 ? "option" : "presentation"}
              aria-selected={fileIdx >= 0 ? fileIdx === selected : undefined}
              className={`project-search-file-head${fileIdx >= 0 && fileIdx === selected ? " selected" : ""}`}
              data-testid={`project-search-file-${f.path}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onOpen(fileItem)}
            >
              <span className="project-search-name">{name}</span>
              <span className="muted small project-search-dir">{dir}</span>
            </div>
            {f.matches.map((m) => {
              const item: SearchItem = {
                kind: "match",
                key: `m:${f.path}:${m.line}:${m.column}`,
                path: f.path,
                match: m,
              };
              const idx = indexOf.get(item.key) ?? -1;
              const [before, hit, after] = splitHighlight(m.text, m.textStart, m.length);
              return (
                <div
                  key={item.key}
                  role="option"
                  aria-selected={idx === selected}
                  className={`project-search-match${idx === selected ? " selected" : ""}`}
                  data-testid={`project-search-match-${f.path}:${m.line}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onOpen(item)}
                >
                  <span className="project-search-line">{m.line}</span>
                  <span className="project-search-snippet">
                    {before}
                    <mark>{hit}</mark>
                    {after}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
}
